import React, { useEffect, useState } from "react";
import { CustomInputFieldSelect } from "../../common/FormInputs";
import CaseService from "../../../services/CaseService";
import styled from "styled-components";
import theme from "../../../assets/theme";
import COLORS from "../../../assets/Colors";
import { Autocomplete } from "@material-ui/lab";
import { Checkbox, Chip } from "@material-ui/core";
import CheckBoxOutlineBlankIcon from "@material-ui/icons/CheckBoxOutlineBlank";
import CheckBoxIcon from "@material-ui/icons/CheckBox";

const MultipleCustomSelect = ({ id, caseIds, setCaseIds }) => {
  const [caseList, setCaseList] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const response = await CaseService.getAgentCases(id);
        if (response?.caseDet) {
          const cases = response.caseDet;
          setCaseList(cases);
          // Auto-select already assigned cases
          const assigned = cases.filter((item) => item.assigned);
          setCaseIds(assigned);
        }
      } catch (error) {
        console.error("Failed to load cases:", error);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const allCases = caseList;
  const isAllSelected =
    allCases.length > 0 && caseIds.length === allCases.length;
  const isIndeterminate =
    caseIds.length > 0 && caseIds.length < allCases.length;

  const handleChange = (event, newValue) => {
    // Handle "Select All" logic if you later add it
    setCaseIds(newValue);
  };

  const handleSelectAll = () => {
    setCaseIds(isAllSelected ? [] : [...allCases]);
  };

  return (
    <Container>
      <Autocomplete
        multiple
        options={allCases}
        disableCloseOnSelect
        value={caseIds}
        onChange={handleChange}
        getOptionLabel={(option) => option.title || `Case ${option.id}`}
        noOptionsText="No cases available"
        renderOption={(option, { selected }) => (
          <OptionItem>
            <Checkbox
              icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
              checkedIcon={<CheckBoxIcon fontSize="small" />}
              checked={selected}
              style={{ color: "#00838c" }}
            />
            <OptionText>{option.title || `Case ${option.id}`}</OptionText>
          </OptionItem>
        )}
        renderTags={(selected, getTagProps) =>
          selected.map((option, index) => (
            <StyledChip
              key={option.id}
              label={option.title || option.id}
              {...getTagProps({ index })}
              onDelete={getTagProps({ index }).onDelete}
            />
          ))
        }
        renderInput={(params) => (
          <CustomInputFieldSelect
            {...params}
            variant="outlined"
            placeholder={
              caseIds.length === 0 ? "Search and select cases..." : ""
            }
            className="input-white"
          />
        )}
      />

      {/* Select All Toggle */}
      {allCases.length > 0 && (
        <SelectAllContainer onClick={handleSelectAll}>
          <Checkbox
            checked={isAllSelected}
            indeterminate={isIndeterminate}
            icon={<CheckBoxOutlineBlankIcon />}
            checkedIcon={<CheckBoxIcon />}
            indeterminateIcon={<IndeterminateIcon />}
            style={{ color: "#00838c" }}
            size="small"
          />
          <SelectAllLabel>
            {isAllSelected
              ? "Deselect All"
              : isIndeterminate
              ? `${caseIds.length} selected`
              : `Select All (${allCases.length} cases)`}
          </SelectAllLabel>
        </SelectAllContainer>
      )}

      {/* Selected Summary */}
      {caseIds.length > 0 && (
        <Summary>
          <strong>{caseIds.length}</strong> case{caseIds.length > 1 ? "s" : ""}{" "}
          selected
        </Summary>
      )}
    </Container>
  );
};

export default MultipleCustomSelect;

/* CLEAN & MODERN STYLED COMPONENTS */

const Container = styled.div`
  width: 100%;
`;

const OptionItem = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  padding: 6px 0;
  transition: background 0.2s;

  &:hover {
    background-color: #f5f5f5;
  }
`;

const OptionText = styled.span`
  margin-left: 12px;
  font-family: ${theme.fonts.primaryFontRegular};
  font-size: 14px;
  color: ${COLORS.COLOR_DARK};
`;

const StyledChip = styled(Chip)`
  && {
    background-color: #00838c;
    color: white;
    font-family: ${theme.fonts.primaryFontSemiBold};
    font-size: 12px;
    height: 28px;
    margin: 4px 6px 4px 0;

    .MuiChip-deleteIcon {
      color: rgba(255, 255, 255, 0.8);
      &:hover {
        color: white;
      }
    }
  }
`;

const SelectAllContainer = styled.div`
  display: flex;
  align-items: center;
  margin-top: 12px;
  padding: 10px 12px;
  background-color: #f8f9fa;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s;
  border: 1px solid #e0e0e0;

  &:hover {
    background-color: #e9ecef;
    border-color: #00838c;
  }
`;

const SelectAllLabel = styled.span`
  margin-left: 12px;
  font-family: ${theme.fonts.primaryFontSemiBold};
  font-size: 14px;
  color: ${COLORS.COLOR_DARK};
`;

const Summary = styled.div`
  margin-top: 12px;
  padding: 10px 14px;
  background-color: #e3f2fd;
  color: #1976d2;
  font-family: ${theme.fonts.primaryFontSemiBold};
  font-size: 14px;
  text-align: center;
  border-radius: 8px;
  border-left: 4px solid #1976d2;
`;

// Custom Indeterminate Icon
const IndeterminateIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24">
    <rect x="5" y="11" width="14" height="2" fill="#1976d2" />
    <rect
      x="3"
      y="3"
      width="18"
      height="18"
      rx="2"
      fill="none"
      stroke="#1976d2"
      strokeWidth="2"
    />
  </svg>
);
